import {Component, effect} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {finalize} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {TranslateModule} from '@ngx-translate/core';
import {AConcreteResource} from '../../../../AConcreteResource';
import {CustomerModel} from '../../../../../models/resources/customer.model';
import {CustomerService} from '../../../../../services/resources/customer.service';
import {HttpErrorsService} from '../../../../../services/utils/http-errors.service';
import {CustomNotificationService} from '../../../../../services/utils/custom-notification.service';
import {SafeUpdateService} from '../../../../../services/utils/safe-update.service';
import {MdbButtonComponent} from '../../../../utils/mdb-button/mdb-button.component';
import {SpinnerComponent} from '../../../../utils/spinner/spinner.component';
import {FormInputComponent} from '../../../../utils/form-input/form-input.component';
import {CheckboxInputComponent} from '../../../../utils/checkbox-input/checkbox-input.component';
import {EnumSelectComponent} from '../../../../utils/enum-select/enum-select.component';
import {AutopostBills, castAutopostBillsEnum} from '../../../../../enums/autopost-bills';

/**
 * Tab "Buchhaltung" – bildet den gleichnamigen Odoo-Tab am Customer-Model ab.
 * Bankkonten kommen aus dem Odoo-Sync und werden nur angezeigt.
 */
@Component({
  selector: 'app-customer-accounting',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MdbButtonComponent,
    SpinnerComponent,
    FormInputComponent,
    CheckboxInputComponent,
    EnumSelectComponent
  ],
  templateUrl: './customer-accounting.component.html',
  styleUrl: './customer-accounting.component.scss',
})
export class CustomerAccountingComponent extends AConcreteResource<CustomerModel> {
  loading: boolean = false;
  formGroup: FormGroup;

  readonly autopostBillsEnum = AutopostBills;
  readonly castAutopostBills = castAutopostBillsEnum;

  constructor(
    router: Router,
    private customerService: CustomerService,
    private httpErrorsService: HttpErrorsService,
    private notificationService: CustomNotificationService,
    private safeUpdateService: SafeUpdateService,
    private formBuilder: FormBuilder
  ) {
    super(router, customerService);

    this.formGroup = this.formBuilder.group({
      iso20022Lei: [null],
      autopostBills: [AutopostBills.ASK],
      ignoreAbnormalInvoiceAmount: [false],
      ignoreAbnormalInvoiceDate: [false],
      usePartnerCreditLimit: [false],
      creditLimit: [null, [Validators.min(0)]],
    });

    effect(() => {
      const model = this.model();
      if (model) {
        this.formGroup.patchValue({
          iso20022Lei: model.iso20022Lei ?? null,
          autopostBills: model.autopostBills ?? AutopostBills.ASK,
          ignoreAbnormalInvoiceAmount: model.ignoreAbnormalInvoiceAmount ?? false,
          ignoreAbnormalInvoiceDate: model.ignoreAbnormalInvoiceDate ?? false,
          usePartnerCreditLimit: model.usePartnerCreditLimit ?? false,
          creditLimit: model.creditLimit ?? null,
        });
      }
    });
  }

  submit(serverResource: any = null, overrideValues: { [key: string]: any } = {}) {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) return;

    this.loading = true;

    const model = this.model()!;
    let resource: any = {...this.formGroup.value};

    // Pflichtfelder der Customer-Validierung + Safe-Update
    resource.companyType = model.companyType;
    resource.company = model.company;
    resource.updatedAt = model.updatedAt;

    // Update-Validierung verlangt id/updatedAt der 1:1-Relationen (Safe-Update)
    if (model.person?.id)
      resource.person = {id: model.person.id, updatedAt: model.person.updatedAt};
    if (model.address?.id)
      resource.address = {id: model.address.id, updatedAt: model.address.updatedAt};

    if (serverResource)
      resource = this.safeUpdateService.handleResource(resource, serverResource, overrideValues);

    this.customerService
      .update(model.id, resource)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.notificationService.success('CHANGES_SUCCESSFULLY_SAVED');
        },
        error: (e: HttpErrorResponse) => {
          this.httpErrorsService.handleError(e, (newServerResource, newOverrideValues) => {
            this.submit(newServerResource, newOverrideValues);
          });
        },
      });
  }
}
